import React, { memo, useCallback } from 'react';
import { ScrollView, View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { Hoverable } from '@/components/shared/redesign/Hoverable';

export type MarketplaceCategory = 'All' | 'Furniture' | 'Electronics' | 'Clothing' | 'Free';

const CATEGORIES: MarketplaceCategory[] = ['All', 'Furniture', 'Electronics', 'Clothing', 'Free'];

interface MarketplaceCategoryFilterProps {
  selectedCategory: MarketplaceCategory;
  onSelectCategory: (category: MarketplaceCategory) => void;
  inline?: boolean;
}

export const MarketplaceCategoryFilter: React.FC<MarketplaceCategoryFilterProps> = memo(({
  selectedCategory,
  onSelectCategory,
  inline = false,
}) => {
  const chips = CATEGORIES.map((category) => (
    <CategoryChip
      key={category}
      label={category}
      isSelected={selectedCategory === category}
      onPress={() => onSelectCategory(category)}
    />
  ));

  if (inline) {
    return (
      <View
        style={styles.inlineWrapper}
        accessibilityRole="toolbar"
        accessibilityLabel="Category filters"
      >
        {chips}
      </View>
    );
  }

  return (
    <View style={styles.wrapper} accessibilityRole="toolbar" accessibilityLabel="Category filters">
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        {chips}
      </ScrollView>
    </View>
  );
});

MarketplaceCategoryFilter.displayName = 'MarketplaceCategoryFilter';

const CategoryChip: React.FC<{
  label: string;
  isSelected: boolean;
  onPress: () => void;
}> = memo(({ label, isSelected, onPress }) => {
  const { colors } = useThemeColors();
  const handlePress = useCallback(() => onPress(), [onPress]);

  return (
    <Hoverable
      hover={isSelected ? 'none' : 'glass'}
      style={[
        styles.chip,
        isSelected
          ? { backgroundColor: colors.newsfeed.tagActiveBg, borderColor: colors.newsfeed.tagActiveBorder }
          : { backgroundColor: colors.glass.buttonBg, borderColor: colors.glass.buttonBorder },
      ]}
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ selected: isSelected }}
    >
      <Text style={[styles.chipText, { color: isSelected ? colors.newsfeed.tagActiveText : '#888' }]}>
        {label}
      </Text>
    </Hoverable>
  );
});

CategoryChip.displayName = 'CategoryChip';

const styles = StyleSheet.create({
  wrapper: {
    paddingVertical: 4,
  },
  inlineWrapper: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
  },
  scrollContent: {
    gap: 8,
    alignItems: 'center',
  },
  chip: {
    paddingHorizontal: 17,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
  },
  chipText: {
    ...REDESIGN_TYPOGRAPHY.tagFilter,
  },
});
